import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import employeeService from '../../services/employeeService';
import { CrudStyles } from '../shared/Crud';
import { PaginationContainer as CrudPaginationBar } from '../shared/Crud/styles';
import { SELECTOR_MODAL_HINT, SelectorModalHint } from './SelectorModalHint';

const { Button, PageInfo, PageButtons } = CrudStyles;

const ModalPagination = styled(CrudPaginationBar)`
    flex-shrink: 0;
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 16px;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
`;

const ModalBox = styled.div`
    box-sizing: border-box;
    background: white;
    padding: 24px;
    border-radius: 12px;
    width: 100%;
    max-width: min(520px, calc(100vw - 32px));
    max-height: min(90vh, 90dvh);
    overflow: hidden;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    min-width: 0;
    flex-shrink: 0;
    h2 {
        margin: 0;
        font-size: 18px;
        color: ${({ theme }) => theme.colors.gray?.[900] || '#222'};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const CloseBtn = styled.button`
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #666;
    font-size: 24px;
    line-height: 1;
    &:hover {
        color: #222;
    }
`;

const TableWrap = styled.div`
    flex: 1;
    min-height: 0;
    margin-bottom: 16px;
    min-width: 0;
    overflow-x: auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
`;

const ModalTable = styled.table`
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    th, td {
        padding: 12px 16px;
        text-align: left;
        border-bottom: 1px solid ${({ theme }) => theme.colors?.gray?.[100] || '#eee'};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    th {
        background: ${({ theme }) => theme.colors?.background || '#f5f5f5'};
        font-weight: 600;
        color: ${({ theme }) => theme.colors?.gray?.[900] || '#222'};
    }
    th:first-child,
    td:first-child {
        width: 42%;
    }
    th:last-child,
    td:last-child {
        width: 58%;
    }
`;

const SelectableRow = styled.tr`
    cursor: pointer;
    transition: background 0.15s;
    &:hover {
        background: ${({ theme }) => (theme.colors?.primary?.light ? `${theme.colors.primary.light}18` : 'rgba(94, 129, 172, 0.12)')};
    }
`;

export default function RequestersModal({ customerId, onSelect, onSkip, onClose }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [pageMeta, setPageMeta] = useState({
        totalPages: 0,
        first: true,
        last: true,
    });

    useEffect(() => {
        if (customerId == null) return;
        let cancelled = false;
        setLoading(true);
        employeeService
            .getSolicitantes({ customerId, page, size: 15, sort: 'name,asc' })
            .then((res) => {
                if (cancelled) return;
                const content = res._embedded?.content ?? res.content ?? [];
                const meta = res.page || {};
                const totalPages = content.length === 0 ? 0 : (meta.totalPages ?? 1);
                setData(content);
                setPageMeta({
                    totalPages,
                    first: totalPages === 0 || page === 0,
                    last: totalPages === 0 || page >= totalPages - 1,
                });
            })
            .catch(() => {
                if (!cancelled) setData([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [customerId, page]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSelect = (employee) => {
        onSelect?.(employee);
        onClose?.();
    };

    return (
        <Overlay onClick={(e) => e.target === e.currentTarget && onClose?.()}>
            <ModalBox onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <h2>Quem solicitou a entrega?</h2>
                    <CloseBtn type="button" onClick={onClose} title="Fechar">
                        ×
                    </CloseBtn>
                </ModalHeader>
                <SelectorModalHint>{SELECTOR_MODAL_HINT}</SelectorModalHint>
                <TableWrap>
                    <ModalTable>
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Email</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={2} style={{ textAlign: 'center' }}>
                                        Carregando...
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={2} style={{ textAlign: 'center' }}>
                                        Nenhum solicitante encontrado
                                    </td>
                                </tr>
                            ) : (
                                data.map((row) => (
                                    <SelectableRow
                                        key={row.id}
                                        onClick={() => handleSelect(row)}
                                        title={`${row.name ?? ''} - ${row.email ?? ''}`}
                                    >
                                        <td title={row.name ?? ''}>{row.name ?? '—'}</td>
                                        <td title={row.email ?? ''}>{row.email ?? '—'}</td>
                                    </SelectableRow>
                                ))
                            )}
                        </tbody>
                    </ModalTable>
                </TableWrap>
                <ModalPagination>
                    <PageInfo>
                        Página {pageMeta.totalPages === 0 ? 0 : page + 1} de {pageMeta.totalPages || 0}
                    </PageInfo>
                    <PageButtons>
                        <Button
                            variant="outline"
                            disabled={pageMeta.totalPages === 0 || pageMeta.first}
                            onClick={() => handlePageChange(page - 1)}
                        >
                            Anterior
                        </Button>
                        <Button
                            variant="outline"
                            disabled={pageMeta.totalPages === 0 || pageMeta.last}
                            onClick={() => handlePageChange(page + 1)}
                        >
                            Próxima
                        </Button>
                        {onSkip && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    onSkip?.();
                                    onClose?.();
                                }}
                            >
                                Pular
                            </Button>
                        )}
                    </PageButtons>
                </ModalPagination>
            </ModalBox>
        </Overlay>
    );
}
